import { cn } from "@/lib/utils";
import type { BriefingsTableStatus } from "./briefingsTable.types";

const STATUS_PRESENTATION: Record<
  BriefingsTableStatus,
  {
    label: string;
    railColor: string;
    pillClassName: string;
    dotClassName: string;
  }
> = {
  NEEDS_REVIEW: {
    label: "Needs review",
    railColor: "#ca8a04",
    pillClassName: "bg-[#FFF8EE] text-yellow-600",
    dotClassName: "bg-yellow-600",
  },
  ACTIVE: {
    label: "Active",
    railColor: "#22c55e",
    pillClassName: "bg-[#EDFFF0] text-green-600",
    dotClassName: "bg-green-500",
  },
  SCHEDULED: {
    label: "Scheduled",
    railColor: "#d1d5db",
    pillClassName: "bg-gray-50 text-gray-600",
    dotClassName: "bg-gray-300",
  },
  PAUSED: {
    label: "Paused",
    railColor: "#9ca3af",
    pillClassName: "bg-gray-100 text-gray-700",
    dotClassName: "bg-gray-500",
  },
};

const BRIEFING_STATUS_PILL_WIDTH_PX = 117;

const pillBaseClassName =
  "box-border inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium leading-4 tracking-[0.12px]";

/** Display label for a status (shared by the pill and the status tabs). */
export const briefingStatusLabel = (status: BriefingsTableStatus): string =>
  STATUS_PRESENTATION[status].label;

/** Title-column left rail; matches each status dot / accent. */
export const briefingStatusTitleRailColor = (
  status: BriefingsTableStatus,
): string => STATUS_PRESENTATION[status].railColor;

interface BriefingStatusPillProps {
  status: BriefingsTableStatus;
}

const BriefingStatusPill = ({ status }: BriefingStatusPillProps) => {
  const presentation = STATUS_PRESENTATION[status];

  return (
    <span
      className={cn(pillBaseClassName, presentation.pillClassName)}
      style={{ width: BRIEFING_STATUS_PILL_WIDTH_PX }}
    >
      <span
        className={cn(
          "size-1.5 shrink-0 rounded-full",
          presentation.dotClassName,
        )}
        aria-hidden
      />
      {presentation.label}
    </span>
  );
};

export default BriefingStatusPill;
